import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { getPokemonImage, createPokemonImageErrorHandler, useImageMapReady } from '../utils/pokemonImageResolver';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Clean Pokemon Image Gallery with simple Normal/Shiny tabs
 * 
 * Uses pokemonImageResolver.js - SINGLE SOURCE OF TRUTH for all Pokemon images
 */
const PokemonImageGallery = ({ 
  pokemonId, 
  pokemonName = '', 
  pokemonNameTh = '',
  hasShiny = false,
  type1 = '',
  className = ''
}) => {
  const { t } = useLanguage();
  const imageMapReady = useImageMapReady(); // Triggers re-render when image map loads
  const [showShiny, setShowShiny] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isIngameSprite, setIsIngameSprite] = useState(false);

  // Use pokemonImageResolver - SINGLE SOURCE OF TRUTH
  // imageMapReady dependency ensures re-computation after async map loads
  const normalImageUrl = useMemo(() => {
    if (!pokemonId) return '/images/pokemon-spirit/000.png';
    return getPokemonImage(pokemonId, 'default');
  }, [pokemonId, imageMapReady]);
  
  const shinyImageUrl = useMemo(() => {
    if (!pokemonId || !hasShiny) return '/images/pokemon-spirit/000.png';
    return getPokemonImage(pokemonId, 'shiny');
  }, [pokemonId, hasShiny, imageMapReady]);
  
  const handleNormalImageError = createPokemonImageErrorHandler(pokemonId, { variant: 'default' });
  const handleShinyImageError = createPokemonImageErrorHandler(pokemonId, { variant: 'shiny' });

  // Get type-based gradient colors
  const getTypeGradient = (type) => {
    const gradients = {
      'Fire': 'from-orange-400 to-red-500',
      'Water': 'from-cyan-400 to-blue-500',
      'Grass': 'from-green-400 to-emerald-500',
      'Electric': 'from-yellow-300 to-amber-400',
      'Psychic': 'from-pink-400 to-purple-500',
      'Ice': 'from-cyan-300 to-sky-400',
      'Dragon': 'from-indigo-400 to-purple-600',
      'Dark': 'from-gray-600 to-gray-800',
      'Fairy': 'from-pink-300 to-rose-400',
      'Fighting': 'from-orange-500 to-red-600',
      'Poison': 'from-purple-400 to-violet-500',
      'Ground': 'from-amber-400 to-yellow-600',
      'Flying': 'from-sky-300 to-indigo-400',
      'Bug': 'from-lime-400 to-green-500',
      'Rock': 'from-stone-400 to-amber-600',
      'Ghost': 'from-purple-500 to-indigo-600',
      'Steel': 'from-gray-400 to-slate-500',
      'Normal': 'from-gray-300 to-gray-400'
    };
    return gradients[type] || 'from-gray-300 to-gray-400';
  };

  const currentImage = showShiny ? shinyImageUrl : normalImageUrl;

  // Reset loaded state when image URL actually changes (covers pokemonId, shiny toggle, AND imageMap load)
  const prevImageRef = React.useRef(currentImage);
  useEffect(() => {
    if (prevImageRef.current !== currentImage) {
      prevImageRef.current = currentImage;
      setImageLoaded(false);
      setHasError(false);
      setIsIngameSprite(false);
    }
  }, [currentImage]);
  const currentImageErrorHandler = showShiny ? handleShinyImageError : handleNormalImageError;
  
  // Create a unique key that includes pokemonId to force re-render when Pokemon changes
  const imageKey = `${pokemonId}-${showShiny ? 'shiny' : 'normal'}`;
  
  // Check if current image is an ingame sprite (256x256)
  const isCurrentIngame = currentImage.includes('pokemon_images_ingame');

  const handleImageLoad = () => {
    setImageLoaded(true);
    setIsIngameSprite(isCurrentIngame);
  };

  // Check if this is a Shadow Pokemon
  const isShadow = pokemonId?.toLowerCase().includes('shadow') || pokemonName?.toLowerCase().includes('shadow');

  return (
    <div className={`relative ${className}`}>
      {/* Main Image Container - Optimized for clarity */}
      <div 
        className={`relative w-full max-w-[280px] mx-auto aspect-square rounded-2xl overflow-hidden p-0.5 shadow-lg ${
          isShadow ? '' : `bg-gradient-to-br ${getTypeGradient(type1)}`
        }`}
        style={isShadow ? {
          backgroundImage: 'url(/images/rocket-ball.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : {}}
      >
        {/* Inner container with subtle gradient */}
        <div className={`relative w-full h-full rounded-[14px] overflow-hidden ${
          isShadow ? '' : 'bg-gradient-to-b from-white to-gray-50'
        }`}>
          {/* Pokemon Image */}
          <div className="relative w-full h-full flex items-center justify-center p-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={imageKey}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                className="relative w-full h-full flex items-center justify-center"
              >
                {!hasError ? (
                  <>
                    {/* Loading placeholder - shows behind image until loaded */}
                    {!imageLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center z-0">
                        <div className="w-16 h-16 rounded-full bg-gray-100 animate-pulse" />
                      </div>
                    )}
                    <img
                      key={imageKey}
                      src={currentImage}
                      alt={`${pokemonName}${showShiny ? ' Shiny' : ''}`}
                      className="max-w-full max-h-full object-contain relative z-10"
                      style={{
                        imageRendering: isCurrentIngame ? 'crisp-edges' : 'auto',
                        WebkitImageRendering: isCurrentIngame ? 'crisp-edges' : 'auto',
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))'
                      }}
                      onLoad={handleImageLoad}
                      onError={currentImageErrorHandler}
                      loading="eager"
                    />
                    
                    {/* Subtle shiny sparkle */}
                    {showShiny && imageLoaded && (
                      <motion.div
                        animate={{ opacity: [0.4, 0.8, 0.4] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                        className="absolute top-2 right-2"
                      >
                        <Sparkles className="w-5 h-5 text-yellow-400" />
                      </motion.div>
                    )}
                  </>
                ) : (
                  <div className="text-4xl opacity-20">❓</div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Simple Tab Selector - Only show if has shiny */}
      {hasShiny && (
        <div className="mt-3 max-w-[280px] mx-auto flex rounded-xl bg-gray-100 p-1">
          <button
            onClick={() => setShowShiny(false)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
              !showShiny 
                ? 'bg-white text-gray-800 shadow-sm' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('gallery.normal')}
          </button>
          <button
            onClick={() => setShowShiny(true)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-1 ${
              showShiny 
                ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white shadow-sm' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            {t('gallery.shiny')}
          </button>
        </div>
      )}
    </div>
  );
};

export default PokemonImageGallery;

